var assert = require('nanoassert')
var clone = require('clone')
var { SymmetricState } = require('./symmetric-state')
var dh = require('./dh')

const TOK_S = Symbol('s')
const TOK_E = Symbol('e')
const TOK_ES = Symbol('es')
const TOK_SE = Symbol('se')
const TOK_EE = Symbol('ee')
const TOK_SS = Symbol('ss')
const TOK_PSK = Symbol('psk')

// initiator, ->
// responder, <-
var PATTERNS = Object.freeze({
  N: {
    initiatorPremessages: [],
    responderPremessages: [TOK_S],
    messagePatterns: [
      [TOK_E, TOK_ES]
    ]
  },
  K: {
    initiatorPremessages: [TOK_S],
    responderPremessages: [TOK_S],
    messagePatterns: [
      [TOK_E, TOK_ES, TOK_SS]
    ]
  },
  X: {
    initiatorPremessages: [],
    responderPremessages: [TOK_S],
    messagePatterns: [
      [TOK_E, TOK_ES, TOK_S, TOK_SS]
    ]
  },
  NN: {
    initiatorPremessages: [],
    responderPremessages: [],
    messagePatterns: [
      [TOK_E],
      [TOK_E, TOK_EE]
    ]
  },
  KN: {
    initiatorPremessages: [TOK_S],
    responderPremessages: [],
    messagePatterns: [
      [TOK_E],
      [TOK_E, TOK_EE, TOK_SE]
    ]
  },
  NK: {
    initiatorPremessages: [],
    responderPremessages: [TOK_S],
    messagePatterns: [
      [TOK_E, TOK_ES],
      [TOK_E, TOK_EE]
    ]
  },
  KK: {
    initiatorPremessages: [TOK_S],
    responderPremessages: [TOK_S],
    messagePatterns: [
      [TOK_E, TOK_ES, TOK_SS],
      [TOK_E, TOK_EE, TOK_SE]
    ]
  },
  NX: {
    initiatorPremessages: [],
    responderPremessages: [],
    messagePatterns: [
      [TOK_E],
      [TOK_E, TOK_EE, TOK_S, TOK_ES]
    ]
  },
  KX: {
    initiatorPremessages: [TOK_S],
    responderPremessages: [],
    messagePatterns: [
      [TOK_E],
      [TOK_E, TOK_EE, TOK_SE, TOK_S, TOK_ES]
    ]
  },
  XN: {
    initiatorPremessages: [],
    responderPremessages: [],
    messagePatterns: [
      [TOK_E],
      [TOK_E, TOK_EE],
      [TOK_S, TOK_SE]
    ]
  },
  IN: {
    initiatorPremessages: [],
    responderPremessages: [],
    messagePatterns: [
      [TOK_E, TOK_S],
      [TOK_E, TOK_EE, TOK_SE]
    ]
  },
  XK: {
    initiatorPremessages: [],
    responderPremessages: [TOK_S],
    messagePatterns: [
      [TOK_E, TOK_ES],
      [TOK_E, TOK_EE],
      [TOK_S, TOK_SE]
    ]
  },
  IK: {
    initiatorPremessages: [],
    responderPremessages: [TOK_S],
    messagePatterns: [
      [TOK_E, TOK_ES, TOK_S, TOK_SS],
      [TOK_E, TOK_EE, TOK_SE]
    ]
  },
  XX: {
    initiatorPremessages: [],
    responderPremessages: [],
    messagePatterns: [
      [TOK_E],
      [TOK_E, TOK_EE, TOK_S, TOK_ES],
      [TOK_S, TOK_SE]
    ]
  },
  IX: {
    initiatorPremessages: [],
    responderPremessages: [],
    messagePatterns: [
      [TOK_E, TOK_S],
      [TOK_E, TOK_EE, TOK_SE, TOK_S, TOK_ES]
    ]
  }
})

class HandshakeState {
  constructor(handshakePattern, initiator, prologue, s, e, rs, re, psks) {
    assert(!s || s.length === dh.SKLEN, 'invalid s')
    assert(!e || e.length === dh.SKLEN, 'invalid e')
    assert(!rs || rs.length === dh.PKLEN, 'invalid rs')
    assert(!re || re.length === dh.PKLEN, 'invalid re')
    assert(!psks || psks.length <= 4, 'invalid psk')

    var modifiers = psks ? psks.map((v, i) => v ? `psk${i}` : null).filter(v => !!v).join('+') : ''
    var protocolName = Buffer.from(`Noise_${handshakePattern}${modifiers}_25519_AESGCM_SHA256`)
    this.symmetricState = new SymmetricState(protocolName)
    this.symmetricState.mixHash(prologue)
    this.initiator = initiator
    this.s = s ? dh.keyPair.fromSecretKey(s) : null
    this.e = e ? dh.keyPair.fromSecretKey(e) : null
    this.rs = rs
    this.re = re

    var pattern = PATTERNS[handshakePattern]

    for (var token of clone(pattern.initiatorPremessages)) {
      switch (token) {
        case TOK_E:
          assert(initiator ? this.e.publicKey : re)
          this.symmetricState.mixHash(initiator ? this.e.publicKey : re)
          break
        case TOK_S:
          assert(initiator ? this.s.publicKey : rs)
          this.symmetricState.mixHash(initiator ? this.s.publicKey : rs)
          break
        default:
          throw new Error('Invalid premessage pattern')
      }
    }

    for (var token of clone(pattern.responderPremessages)) {
      switch (token) {
        case TOK_E:
          assert(initiator ? re : this.e.publicKey)
          this.symmetricState.mixHash(initiator ? re : this.e.publicKey)
          break
        case TOK_S:
          assert(initiator ? rs : this.s.publicKey)
          this.symmetricState.mixHash(initiator ? rs : this.s.publicKey)
          break
        default:
          throw new Error('Invalid premessage pattern')
      }
    }

    this.messagePatterns = clone(pattern.messagePatterns)

    if (psks) {
      this.psks = psks.filter((v, i) => {
        if (v) {
          assert(v.length, 32, "invalid psk")
          if (i === 0) {
            this.messagePatterns[0].unshift(TOK_PSK)
          } else {
            this.messagePatterns[i - 1].push(TOK_PSK)
          }
          return true
        }
        return false
      })
    } else {
      this.psks = null
    }

    this.messagePatterns.forEach((tks, i) => {
      if (i % 2 == (initiator ? 0 : 1) && tks.includes(TOK_S) && !this.s) {
        throw new Error("this handshake pattern requires a static keypair")
      }
    })
  }

  writeMessage(payload) {
    var tokens = this.messagePatterns.shift()
    assert(tokens)

    var out = []
    for (var token of tokens) {
      switch (token) {
        case TOK_E:
          assert(this.e == null)
          this.e = dh.generateKeypair()
          out.push(this.e.publicKey)
          this.symmetricState.mixHash(this.e.publicKey)
          if (this.psks) {
            this.symmetricState.mixKey(this.e.publicKey)
          }
          break

        case TOK_S:
          assert(this.s.publicKey.length === dh.PKLEN)
          out.push(this.symmetricState.encryptAndHash(this.s.publicKey))
          break

        case TOK_PSK:
          var psk = this.psks.shift()
          assert(psk && psk.length === 32)
          this.symmetricState.mixKeyAndHash(psk);
          break

        case TOK_EE:
          this.symmetricState.mixKey(dh(this.e.secretKey, this.re))
          break

        case TOK_ES:
          if (this.initiator) {
            this.symmetricState.mixKey(dh(this.e.secretKey, this.rs))
          } else {
            this.symmetricState.mixKey(dh(this.s.secretKey, this.re))
          }
          break

        case TOK_SE:
          if (this.initiator) {
            this.symmetricState.mixKey(dh(this.s.secretKey, this.re))
          } else {
            this.symmetricState.mixKey(dh(this.e.secretKey, this.rs))
          }
          break

        case TOK_SS:
          this.symmetricState.mixKey(dh(this.s.secretKey, this.rs))
          break

        default:
          throw new Error('Invalid message pattern')
      }
    }

    out.push(this.symmetricState.encryptAndHash(payload))

    return Buffer.concat(out)
  }

  readMessage(message) {
    assert(message.length)
    var tokens = this.messagePatterns.shift()
    assert(tokens)

    var offset = 0
    var out = []
    for (var token of tokens) {
      switch (token) {
        case TOK_E:
          assert(this.re == null)
          assert(message.length - offset >= dh.PKLEN)
          this.re = message.slice(offset, offset + dh.PKLEN)
          offset += dh.PKLEN
          this.symmetricState.mixHash(this.re)
          if (this.psks) {
            this.symmetricState.mixKey(this.re)
          }
          break

        case TOK_S:
          assert(this.rs == null)
          var expected = dh.PKLEN
          if (this.symmetricState.cipherState.hasKey()) {
            expected += 16
          }
          assert(message.length - offset >= expected)
          this.rs = this.symmetricState.decryptAndHash(message.slice(offset, offset + expected))
          assert(this.rs && this.rs.length === dh.PKLEN)
          offset += expected
          break

        case TOK_PSK:
          var psk = this.psks.shift()
          assert(psk && psk.length === 32)
          this.symmetricState.mixKeyAndHash(psk);
          break

        case TOK_EE:
          this.symmetricState.mixKey(dh(this.e.secretKey, this.re))
          break

        case TOK_ES:
          if (this.initiator) {
            this.symmetricState.mixKey(dh(this.e.secretKey, this.rs))
          } else {
            this.symmetricState.mixKey(dh(this.s.secretKey, this.re))
          }
          break

        case TOK_SE:
          if (this.initiator) {
            this.symmetricState.mixKey(dh(this.s.secretKey, this.re))
          } else {
            this.symmetricState.mixKey(dh(this.e.secretKey, this.rs))
          }
          break

        case TOK_SS:
          this.symmetricState.mixKey(dh(this.s.secretKey, this.rs))
          break

        default:
          throw new Error('Invalid message pattern')
      }
    }

    return this.symmetricState.decryptAndHash(message.slice(offset))
  }

  split() {
    assert(this.messagePatterns.length == 0)
    return this.symmetricState.split()
  }
}

function initialize(handshakePattern, initiator, prologue, s, e, rs, re, psks) {
  return new HandshakeState(handshakePattern, initiator, prologue, s, e, rs, re, psks)
}

module.exports = { HandshakeState, initialize }